import { FC, ReactNode, useCallback } from 'react';
import { useFormContext, get } from 'react-hook-form';
import styled from 'styled-components';
import { Colors } from '../../../assets';
import { FontSize, FontWeight, Spacing } from '../../../assets/GlobalStyle/Variables';
import { ValidationRule } from './validate';

type FieldProps = {
  id: string;
  name: string;
  label?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  required?: boolean;
  validate?: ValidationRule[];
  maxLength?: number;
};

export const Field: FC<FieldProps> = (props) => {
  const { id, name, placeholder, disabled = false, label, type = 'text', required = false, validate, maxLength } = props;

  const methods = useFormContext();
  const error = get(methods.formState.errors, name);
  const validators = validate ?? [];

  const validation = useCallback(async (value: unknown) => {
    const formValues = methods.getValues() as Record<string, unknown>;

    for (const rule of validators) {
      const result = await rule(value, formValues);
      if (result !== true) {
        return result;
      }
    }

    return true;
  }, [validators, methods]);

  return (
    <StyledFieldWrapper>
      {label && (
        <StyledLabel htmlFor={id}>
          {label}:{required && ' *'}
        </StyledLabel>
      )}
      <StyledInputWrapper>
        <StyledField
          type={type}
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          $hasError={!!error}
          {...methods.register(name, {
            required: required && 'Povinné pole',
            validate: validation,
            ...(maxLength ? { maxLength: { value: maxLength, message: `Max ${maxLength} znaků` } } : {})
          })}
        />
        {error?.message && <span role="alert">{String(error.message)}</span>}
      </StyledInputWrapper>
    </StyledFieldWrapper>
  );
};

const StyledFieldWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  margin-bottom: ${Spacing(8)};
`;

const StyledInputWrapper = styled.div`
  width: 100%;

  span[role='alert'] {
    display: block;
    margin: 2px 0;
    color: ${Colors.error};
    font-size: ${FontSize.small};
  }
`;

const StyledField = styled.input<{ $hasError?: boolean }>`
  padding: 0 ${Spacing(8)};
  border: 1px solid ${(props) => (props.$hasError ? Colors.error : Colors.disabledHover)};
  line-height: 40px;
  height: 42px;
  box-sizing: border-box;
  width: 100%;
  border-radius: 0px;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};

  &:focus {
    outline: none;
    border: 1px solid ${(props) => (props.$hasError ? Colors.error : Colors.disabledHover)};
  }
`;

const StyledLabel = styled.label`
  margin: 0;
  color: ${Colors.grey};
  font-weight: ${FontWeight.strong};
  font-size: ${FontSize.normal};
  white-space: nowrap;
  min-width: 200px;
`;
